'use client'

import { X } from 'lucide-react'
import { useRef, useState } from 'react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import SearchInput from './search-input'
import { Button } from '@/components/ui/button'
import Image from 'next/image'
import Text from '../text'
import SearchResult from './search-result'
import { useSearch } from '@/lib/queries/search'
import SearchLoader from './search-loader'
import SearchEmptyResult from './search-empty-result'
import { Dictionary } from '@/localization/dictionary'

interface SearchViewProps {
  dict?: Dictionary['searchView']
}

const SearchView = ({ dict }: SearchViewProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchInput, setSearchInput] = useState<string>('')

  const { data, isLoading } = useSearch(searchInput)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const handleOnOpenChange = (isOpen: boolean) => {
    setIsOpen(isOpen)
    if (isOpen) setTimeout(() => searchInputRef?.current?.focus(), 100)
    else setSearchInput('')
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOnOpenChange}>
      <DialogTrigger asChild>
        <Button variant={'plain'} size={'plain'}>
          <Image
            priority
            src={'/icons/search.svg'}
            height={24}
            width={24}
            alt={'Search'}
            className={`size-6 cursor-pointer`}
          />
        </Button>
      </DialogTrigger>

      <DialogContent
        aria-describedby={undefined}
        className="flex h-full w-full max-w-none flex-col items-center overflow-y-auto bg-transparent p-0 px-5"
        classNameOverlay="bg-black/90"
      >
        <DialogTitle className="sr-only">{dict?.title}</DialogTitle>
        <div className="flex w-full justify-end p-5 sm:px-4 sm:pt-6 md:px-38">
          <DialogClose>
            <X className="size-6 text-white sm:size-12" strokeWidth={1} />
          </DialogClose>
        </div>

        <div className="w-full max-w-[574px] pt-5.5 sm:pt-[17%]">
          <Text
            as="h3"
            className="mb-12 animate-fade-in text-center text-white"
            uppercase
            markdown
          >
            {dict?.title}
          </Text>

          <div>
            <div className="relative">
              <SearchInput
                ref={searchInputRef}
                value={searchInput}
                onChange={setSearchInput}
                placeholder={dict?.title}
              />

              <SearchResult
                data={data}
                setIsOpen={setIsOpen}
                searchInput={searchInput}
                dict={dict?.seeAll}
              />
            </div>

            {!searchInput && (
              <Text
                as="p"
                variant="primary"
                className="mt-1 animate-fade-in text-white"
              >
                {dict?.enterSearchTerm}
              </Text>
            )}

            {isLoading && <SearchLoader />}

            {searchInput && !data?.length && !isLoading && (
              <SearchEmptyResult
                searchInput={searchInput}
                dict={dict?.noResults}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SearchView
